import React, { useContext } from 'react';
import type { TransitionStatus } from 'react-transition-group/Transition';

import Button from '#@/components/Button';
import ButtonGroup from '#@/components/ButtonGroup';
import HoverableElement from '#@/components/HoverableElement';
import { useTranslations } from '#@/modules/translations';
import useQueryMatches from '#@/shared/hooks/useQueryMatches';

import { LightboxControlsContext } from '../controls/Context';
import { LightboxContext } from '../LightboxContext';
import { Container, PageCount, Title } from './Toolbar.styles';

interface Props {
  transitionState: TransitionStatus;
}

export const Toolbar: React.FC<Props> = ({ transitionState }) => {
  const translation = useTranslations('lightbox');

  const { file, images, title, shouldHideDownload, shouldHideOpenInNewTab, isLoading, close } =
    useContext(LightboxContext);
  const controls = useContext(LightboxControlsContext);

  const { isMobile } = useQueryMatches();
  const buttonSize = !isMobile ? 'small' : undefined;

  const isMultiPage = file?.type === 'document' || images;

  const isNextDisabled = isLoading || controls.page === controls.numPages;

  const isPrevDisabled = isLoading || controls.page === 1;

  const isDecreaseDisabled = isLoading || controls.scale.current <= controls.scale.min;

  const isIncreaseDisabled = isLoading || controls.scale.current >= controls.scale.max;

  return (
    <Container $transitionState={transitionState}>
      <>
        <Button onClick={close} hollow icon="arrow-left" size={buttonSize}>
          {translation.back}
        </Button>
        {!isMobile && <Title>{title ?? file?.name}</Title>}
      </>
      <>
        {!isMobile && (
          <>
            <ButtonGroup>
              <HoverableElement
                isTriggerElementClickable={!isIncreaseDisabled}
                tooltipText={translation.zoomIn}
              >
                <Button
                  onClick={() => controls.increaseScale()}
                  disabled={isIncreaseDisabled}
                  iconLeft={{ type: 'zoom-in' }}
                  size={buttonSize}
                  variant="secondary"
                  data-cy-increase-scale
                />
              </HoverableElement>
              <HoverableElement
                isTriggerElementClickable={!isDecreaseDisabled}
                tooltipText={translation.zoomOut}
              >
                <Button
                  onClick={() => controls.decreaseScale()}
                  disabled={isDecreaseDisabled}
                  iconLeft={{ type: 'zoom-out' }}
                  size={buttonSize}
                  variant="secondary"
                  data-cy-decrease-scale
                />
              </HoverableElement>
            </ButtonGroup>
            {isMultiPage && (
              <PageCount>
                {translation[images ? 'imageCount' : 'pageCount']({
                  current: controls.page,
                  total: controls.numPages,
                })}
              </PageCount>
            )}
          </>
        )}
        {isMultiPage && (
          <ButtonGroup>
            <HoverableElement
              isTriggerElementClickable={!isPrevDisabled}
              tooltipText={translation.pageUp}
            >
              <Button
                onClick={controls.previousPage}
                disabled={isPrevDisabled}
                iconLeft={{ type: 'chevron-up' }}
                size={buttonSize}
                variant="secondary"
                data-cy-previous-page
              />
            </HoverableElement>
            <HoverableElement
              isTriggerElementClickable={!isNextDisabled}
              tooltipText={translation.pageDown}
            >
              <Button
                onClick={controls.nextPage}
                disabled={isNextDisabled}
                iconLeft={{ type: 'chevron-down' }}
                size={buttonSize}
                variant="secondary"
                data-cy-next-page
              />
            </HoverableElement>
          </ButtonGroup>
        )}
      </>
      {file?.url && (
        <ButtonGroup>
          {!shouldHideOpenInNewTab && (
            <Button
              linkToExternal={file.openInNewTabUrl}
              target="_blank"
              iconLeft={{ type: 'printer' }}
              size={buttonSize}
              variant="secondary"
            >
              {!isMobile && translation.print}
            </Button>
          )}
          {!shouldHideDownload && (
            <Button linkToExternal={file.url} iconLeft={{ type: 'download' }} size={buttonSize}>
              {!isMobile && translation.download}
            </Button>
          )}
        </ButtonGroup>
      )}
    </Container>
  );
};
